import React, { useMemo, useState } from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Checkbox, Container, Divider, FormControlLabel, Grid, Paper } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { expandToFillParentStyles, TooltipIconButton, useBooleanStateWithToggle } from '@paragon/client-tools';
import { PlateList, PlateOption } from '@paragon/contract-library';
import { hasMaterialsEditPermission } from '@paragon/login-tools';
import { getDefaultPlateListGuid, getPlateManufacturer } from '@paragon/material-lists';

import { setDefaultPlateListGuid } from '../../actions/defaultMaterialSelectionActions';
import {
  archivePlateList,
  duplicatePlateList,
  renamePlateList,
  restorePlateList,
  upsertPlateListPrototype,
} from '../../actions/plateListActions';
import { usePlateList } from '../../selectors/plateListSelectors';
import { UserVisibleError } from '../../UserVisibleError';
import { comboPlateTypesToTangiblePlateTypes, tangiblePlateOptionsToComboPlateOptions } from '../../util/combo-plate';
import { GRID_SPACING } from '../../util/constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ListActions } from '../interface/controls/ListActions';
import { ListForm } from '../interface/forms/ListForm';
import { NavigationBreadcrumbsBase } from '../interface/navigation/NavigationBreadcrumbsBase';
import { fieldsSx } from '../interface/styles';
import { LoadingPage } from './LoadingPage';
import { PlateOptionsGrid } from './PlateOptionsGrid';

const useComboPlateHelpText =
  'ComboPlate allows you to use any plate from the plate list whose size and gauge matches the design. Plates for ' +
  'which this is applicable will be labeled Combo20, Combo18HS, etc. Notes on the TDD and other outputs will clarify ' +
  'the allowed plate types. ComboPlate lists cannot include AnyPlates.';

export function PlateListPage() {
  const defaultPlateListGuid = useAppSelector(getDefaultPlateListGuid);

  const plateListFromState = usePlateList(useParams().plateListGuid);
  const plateOptionsState = useAppSelector((state) => state.plateOptions);

  if (plateOptionsState.state === 'error') throw new UserVisibleError(plateOptionsState.message);

  if (!plateListFromState) throw new UserVisibleError('Plate list could not be found.');
  if (plateListFromState === 'loading' || plateOptionsState.state !== 'loaded') return <LoadingPage />;

  return (
    <UnconnectedPlateListPage
      defaultPlateListGuid={defaultPlateListGuid}
      plateListFromState={plateListFromState}
      plateOptions={plateOptionsState.data}
    />
  );
}

function UnconnectedPlateListPage(props: {
  readonly defaultPlateListGuid: string | null;
  readonly plateListFromState: PlateList;
  readonly plateOptions: readonly PlateOption[];
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasEditPermission = useAppSelector(hasMaterialsEditPermission);

  const [editedPlateList, setEditedPlateList] = useState<PlateList | null>(null);
  const plateList = editedPlateList ?? props.plateListFromState;

  const [showOnlyEnabled, toggleShowOnlyEnabled] = useBooleanStateWithToggle();

  const crumbs = useMemo(
    (): [string, string][] => [
      ['Home', '/'],
      ['Plate Lists', '/'],
      [plateList?.name ?? '', `/plateLists/${plateList?.guid}`],
    ],
    [plateList?.guid, plateList?.name],
  );

  const changesHaveBeenMade = plateList !== props.plateListFromState;

  return (
    <Container sx={{ height: '85%' }}>
      <NavigationBreadcrumbsBase crumbs={crumbs} />

      <ListActions
        type="plate"
        list={plateList}
        defaultListGuid={props.defaultPlateListGuid}
        setDefaultListGuid={setDefaultPlateListGuid}
        archiveList={archivePlateList}
        restoreList={restorePlateList}
        duplicateList={duplicatePlateList}
        renameList={renamePlateList}
      />

      <Divider sx={{ marginBottom: 2 }} />
      <Grid container spacing={GRID_SPACING} sx={{ height: '100%' }}>
        <Grid sx={{ width: '100%' }} size={{ xs: 12, md: 4 }}>
          {hasEditPermission && (
            <ListForm
              pluralNoun="plate pairs"
              list={plateList}
              defaultListGuid={props.defaultPlateListGuid}
              changesHaveBeenMade={changesHaveBeenMade}
              disableSubmission={plateList.plates.length === 0}
              onChange={(changes) => {
                setEditedPlateList((old) => ({ ...(old ?? plateList), ...changes }));
              }}
              onSubmit={async () => {
                await dispatch(upsertPlateListPrototype(plateList));
              }}
              onSetAsDefaultList={async () => {
                await dispatch(setDefaultPlateListGuid(plateList.guid));
              }}
              navigateAfterSubmit={() => void navigate('/')}
            />
          )}
          <Box sx={{ p: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={plateList.useComboPlates}
                  onChange={(event) =>
                    void setEditedPlateList((oldPlateList) => {
                      const workingPlateList = oldPlateList ?? props.plateListFromState;
                      const chosenPlatesWithoutAnyPlates = workingPlateList.plates.filter(
                        (plate) => getPlateManufacturer(plate.plateType) !== 'Any',
                      );
                      return {
                        ...workingPlateList,
                        useComboPlates: event.target.checked,
                        plates: event.target.checked
                          ? tangiblePlateOptionsToComboPlateOptions(chosenPlatesWithoutAnyPlates)
                          : comboPlateTypesToTangiblePlateTypes(workingPlateList.plates),
                      };
                    })
                  }
                />
              }
              label="Make this a ComboPlate List"
            />
            <TooltipIconButton title={useComboPlateHelpText}>
              <HelpIcon />
            </TooltipIconButton>
          </Box>
          <Box sx={{ p: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={showOnlyEnabled} onChange={toggleShowOnlyEnabled} />}
              label="Hide unused plates"
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }} sx={{ ...fieldsSx, ...expandToFillParentStyles.containerStyleProps }}>
          <Paper elevation={0} sx={expandToFillParentStyles.targetStyleProps}>
            <PlateOptionsGrid
              plateOptions={
                plateList.useComboPlates
                  ? props.plateOptions.filter((plateOption) => getPlateManufacturer(plateOption.plateType) !== 'Any')
                  : props.plateOptions
              }
              plateList={plateList}
              setEditedPlateList={(platesPropertyUpdater) =>
                void setEditedPlateList((oldPlateList) => {
                  const workingPlateList = oldPlateList ?? plateList;
                  const tangiblePlateOptions = comboPlateTypesToTangiblePlateTypes(workingPlateList.plates);
                  const updatedTangiblePlateOptions = platesPropertyUpdater(tangiblePlateOptions);
                  const plateOptions = workingPlateList.useComboPlates
                    ? tangiblePlateOptionsToComboPlateOptions(updatedTangiblePlateOptions)
                    : updatedTangiblePlateOptions;
                  return {
                    ...workingPlateList,
                    plates: plateOptions,
                  };
                })
              }
              showOnlyEnabled={showOnlyEnabled}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
